"use client";

import {
    Users,
    Target,
    Trophy,
    Sparkles,
    CloudRain,
    MapPin,
} from "lucide-react";

import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useLanguage } from "@/components/LanguageContext";
import {
    getLocalizedPath,
} from "@/lib/localizedRoutes";

const translations = {
    hu: {
        heroTitle:
            "különleges program barátokkal Budapesten",

        heroSubtitle: (
            <>
                mozi?
                <br />
                beülni valahova?
                <br />
                megint ugyanaz?
            </>
        ),

        heroDescription:
            "ha olyan közös programot kerestek Budapesten, ahol tényleg történik is valami, próbáljátok ki a strike-ot.",

        heroSecond:
            "8 interaktív ügyességi pálya, 48 dobás és egy társaság, amely néhány perc után biztosan komolyan veszi a pontversenyt.",

        bookingButton:
            "foglaljatok időpontot",

        heroImageAlt:
            "program barátokkal a strike interaktív ügyességi játékcentrumában Budapesten",

        togetherTitle:
            "egy közös program, ahol mindenki játszik",

        togetherFirst:
            "a strike-ban nem kell végigülni egy előadást, és nem egy képernyőt néztek.",

        togetherSecond:
            "egymás után teljesítitek a különböző pályákat, dobáltok, pontokat gyűjtötök és folyamatosan látjátok, ki hogyan áll.",

        togetherThird:
            "néhány perc után pedig szinte biztos, hogy valaki elkezdi magyarázni, hogy az előző dobása csak véletlenül sikerült rosszul.",

        gameTitle:
            "8 pálya. 48 dobás.",

        gameIntroduction:
            "egyszerű szabályok, de minden dobással változhat az eredmény.",

        gameItems: [
            {
                title:
                    "8 különböző pálya",

                description:
                    "minden pálya más célfelületekkel és más ügyességi kihívással vár.",
            },
            {
                title:
                    "6 dobás pályánként",

                description:
                    "minden pályán hat lehetőségetek van a lehető legtöbb pontot megszerezni.",
            },
            {
                title:
                    "48 dobás játékosonként",

                description:
                    "egy teljes játék során mindenki összesen 48 alkalommal teheti próbára a pontosságát.",
            },
            {
                title:
                    "pontverseny",

                description:
                    "figyeljétek egymás eredményét, és derüljön ki, ki végez a társaság élén.",
            },
        ],

        whyTitle:
            "miért jó baráti program?",

        whyIntroduction:
            "mert nem csak egymás mellett vagytok, hanem tényleg együtt csináltok valamit.",

        whyItems: [
            "mindenki részt vesz a játékban",
            "nem kell hozzá előzetes tapasztalat",
            "egyszerű szabályokkal indul",
            "folyamatosan lehet egymással versenyezni",
            "beltéri, ezért az időjárás sem számít",
            "egy sima hétköznapra és hétvégi programnak is működik",
        ],

        differentTitle:
            "ha már unjátok a megszokott programokat",

        differentFirst:
            "mozi, étterem, kávézó vagy még egy kör ugyanabban a bárban? néha jó valami mást csinálni.",

        differentSecond:
            "a strike egy aktív, interaktív program, ahol a társaság nem csak beszélget, hanem közben végig játszik is.",

        differentThird:
            "nem bowling, nem szabadulószoba és nem virtuális játék. itt tényleg rajtatok múlik, sikerül-e célba találni.",

        indoorTitle:
            "benti program rossz időre is",

        indoorDescription:
            "ha esik, hideg van vagy egyszerűen nincs kedvetek szabadtéri programhoz, a strike teljesen beltéri élmény.",

        indoorSecond:
            "nem kell B terv az időjárás miatt. válasszatok egy időpontot, hozzátok a társaságot, és kezdődhet a játék.",

        indoorButton:
            "beltéri programok",

        occasionsTitle:
            "nem csak egyféle alkalomra",

        occasionsIntroduction:
            "a strike akkor is működik, ha nincs különösebb apropó.",

        occasionsItems: [
            "hétvégi program barátokkal",
            "munka utáni közös program",
            "születésnap",
            "randi vagy páros program",
            "régi barátok találkozója",
            "spontán esti program",
        ],

        privateTitle:
            "nagyobb társasággal jöttök?",

        privateDescription:
            "ha saját teret is szeretnétek, nézzétek meg a private roomot. a különterem 6–10 fős társaságok számára foglalható, és a foglalás ideje alatt csak a tiétek.",

        privateSecond:
            "jó választás születésnapra, baráti összejövetelre vagy akkor, ha a játék után is együtt maradnátok egy saját térben.",

        privateButton:
            "private room részletek",

        locationTitle:
            "Budapest XIII. kerületében",

        locationDescription:
            "a strike a 1136 Budapest, Pannónia utca 46/A alatt található, könnyen elérhető helyen.",

        locationSecond:
            "a Westend, a Lehel tér és a Jászai Mari tér környékéről is könnyen megközelíthető.",

        locationButton:
            "megközelítés",

        finalTitle:
            "hozd a társaságot",

        finalDescription:
            "8 pálya. 48 dobás. és valaki, aki a végén biztosan túl komolyan veszi a győzelmet.",

        finalSecond:
            "a többit bízd a strike-ra.",

        finalButton:
            "foglaljatok időpontot",
    },

    en: {
        heroTitle:
            "things to do with friends in Budapest",

        heroSubtitle: (
            <>
                cinema?
                <br />
                drinks somewhere?
                <br />
                the same thing again?
            </>
        ),

        heroDescription:
            "if you're looking for something to do with friends in Budapest where something actually happens, try strike.",

        heroSecond:
            "8 interactive skill courses, 48 throws and a group that will probably start taking the score far too seriously within minutes.",

        bookingButton:
            "book your game",

        heroImageAlt:
            "activity with friends at the strike interactive skill game centre in Budapest",

        togetherTitle:
            "an activity everyone actually plays",

        togetherFirst:
            "at strike, you don't sit through a show and you aren't staring at a screen.",

        togetherSecond:
            "you complete the different courses, throw, collect points and follow each other's scores throughout the game.",

        togetherThird:
            "and within a few minutes, someone will almost certainly start explaining why their last bad throw didn't really count.",

        gameTitle:
            "8 courses. 48 throws.",

        gameIntroduction:
            "simple rules, but every throw can change the result.",

        gameItems: [
            {
                title:
                    "8 different courses",

                description:
                    "each course brings different targets and a different skill challenge.",
            },
            {
                title:
                    "6 throws per course",

                description:
                    "you have six chances on every course to collect as many points as possible.",
            },
            {
                title:
                    "48 throws per player",

                description:
                    "during a full game, everyone gets 48 chances to test their accuracy.",
            },
            {
                title:
                    "score competition",

                description:
                    "follow each other's scores and find out who finishes at the top of the group.",
            },
        ],

        whyTitle:
            "why is it great with friends?",

        whyIntroduction:
            "because you're not just together – you're actually doing something together.",

        whyItems: [
            "everyone takes part in the game",
            "no previous experience is required",
            "the rules are easy to understand",
            "you can compete throughout the game",
            "it's indoors, so weather doesn't matter",
            "it works for both weekday and weekend plans",
        ],

        differentTitle:
            "when you're bored of the usual plans",

        differentFirst:
            "cinema, restaurant, coffee or another round at the same bar? sometimes it's good to do something different.",

        differentSecond:
            "strike is an active, interactive experience where your group doesn't just hang out – you're playing the whole time.",

        differentThird:
            "it isn't bowling, an escape room or a virtual game. here, whether you hit the target really is up to you.",

        indoorTitle:
            "an indoor activity for bad weather too",

        indoorDescription:
            "if it's raining, cold or you simply don't feel like being outdoors, strike is a completely indoor experience.",

        indoorSecond:
            "there's no need for a backup plan because of the weather. choose a time, bring the group and start playing.",

        indoorButton:
            "indoor activities",

        occasionsTitle:
            "not just for special occasions",

        occasionsIntroduction:
            "you don't need a particular reason to come to strike.",

        occasionsItems: [
            "weekend activity with friends",
            "after-work plans",
            "birthdays",
            "dates and activities for couples",
            "meeting old friends",
            "spontaneous evening plans",
        ],

        privateTitle:
            "coming with a bigger group?",

        privateDescription:
            "if you'd also like your own space, take a look at the private room. it's available for groups of 6 to 10 and is exclusively yours throughout the booking.",

        privateSecond:
            "a great option for birthdays, gatherings with friends or whenever you'd like to stay together in your own space after playing.",

        privateButton:
            "private room details",

        locationTitle:
            "in Budapest's District XIII",

        locationDescription:
            "strike is located at 46/A Pannónia utca, 1136 Budapest, in an easily accessible location.",

        locationSecond:
            "it's easy to reach from the Westend, Lehel tér and Jászai Mari tér areas.",

        locationButton:
            "getting here",

        finalTitle:
            "bring the group",

        finalDescription:
            "8 courses. 48 throws. and someone who will definitely take winning far too seriously.",

        finalSecond:
            "leave the rest to strike.",

        finalButton:
            "book your game",
    },
};

export default function FriendsPage() {
    const {
        language,
    } =
        useLanguage();

    const text =
        translations[language] ||
        translations.hu;

    return (
        <main className="min-h-screen overflow-x-hidden bg-black font-sans">
            <Header />

            {/* HERO */}
            <section className="bg-[#dedede] text-black">
                <div className="mx-auto flex min-h-[500px] w-full max-w-[430px] flex-col items-center justify-center px-7 py-14 text-center md:max-w-5xl md:min-h-[620px] md:px-12 md:py-20">
                    <IconCircle>
                        <Users
                            style={{
                                width:
                                    "40px",

                                height:
                                    "40px",
                            }}
                            strokeWidth={
                                2.5
                            }
                        />
                    </IconCircle>

                    <h1 className="text-[27px] font-black lowercase leading-[1.05] md:text-[50px]">
                        {text.heroTitle}
                    </h1>

                    <p className="mt-8 text-[19px] font-black leading-[1.15] md:text-[27px]">
                        {text.heroSubtitle}
                    </p>

                    <p className="mt-7 max-w-[325px] text-[15px] leading-[1.3] text-neutral-700 md:max-w-2xl md:text-[21px] md:leading-[1.45]">
                        {text.heroDescription}
                    </p>

                    <p className="mt-6 max-w-[325px] text-[15px] leading-[1.3] text-neutral-700 md:max-w-2xl md:text-[21px] md:leading-[1.45]">
                        {text.heroSecond}
                    </p>

                    <PrimaryButton
                        href="/booking"
                    >
                        {text.bookingButton}
                    </PrimaryButton>
                </div>
            </section>

            <PageImage
                src="/v4/megerkeztel_strike.webp"
                alt={
                    text.heroImageAlt
                }
            />

            {/* KÖZÖS ÉLMÉNY */}
            <ContentSection
                icon={
                    Users
                }
                title={
                    text.togetherTitle
                }
            >
                <div className="mx-auto mt-8 max-w-[330px] space-y-7 text-[15px] leading-[1.3] text-neutral-700 md:max-w-3xl md:text-[21px] md:leading-[1.5]">
                    <p>
                        {text.togetherFirst}
                    </p>

                    <p>
                        {text.togetherSecond}
                    </p>

                    <p>
                        {text.togetherThird}
                    </p>
                </div>
            </ContentSection>

            {/* JÁTÉK */}
            <section className="bg-[#f2f2f2] text-black">
                <div className="mx-auto w-full max-w-[430px] px-5 py-16 md:max-w-5xl md:px-10 md:py-24">
                    <div className="border-[3px] border-black bg-[#f2f2f2]">
                        <div className="bg-black px-6 py-9 text-center text-white md:px-12 md:py-12">
                            <Target
                                className="mx-auto h-10 w-10 md:h-12 md:w-12"
                                strokeWidth={
                                    2.5
                                }
                            />

                            <h2 className="mt-5 text-[24px] font-black lowercase leading-none md:text-[42px]">
                                {text.gameTitle}
                            </h2>

                            <p className="mx-auto mt-5 max-w-[300px] text-[14px] leading-[1.3] text-white/70 md:max-w-xl md:text-[19px]">
                                {text.gameIntroduction}
                            </p>
                        </div>

                        <div className="px-5 py-8 md:px-12 md:py-12">
                            <div className="border-y-[3px] border-black">
                                {text.gameItems.map(
                                    (
                                        item,
                                        index
                                    ) => (
                                        <div
                                            key={
                                                item.title
                                            }
                                            className={`flex items-start gap-4 py-5 md:gap-6 md:py-7 ${index !==
                                                text.gameItems.length - 1
                                                ? "border-b border-black/25"
                                                : ""
                                                }`}
                                        >
                                            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-black text-[11px] font-black text-white md:h-11 md:w-11 md:text-[13px]">
                                                {String(
                                                    index +
                                                    1
                                                ).padStart(
                                                    2,
                                                    "0"
                                                )}
                                            </span>

                                            <div>
                                                <h3 className="text-[16px] font-black lowercase leading-[1.2] md:text-[21px]">
                                                    {item.title}
                                                </h3>

                                                <p className="mt-2 text-[14px] leading-[1.4] text-neutral-700 md:text-[18px] md:leading-[1.5]">
                                                    {item.description}
                                                </p>
                                            </div>
                                        </div>
                                    )
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <PageImage
                src="/v4/találd_meg_eldoradot.webp"
                alt="strike zone interaktív program baráti társaságoknak Budapesten"
            />

            {/* MIÉRT JÓ */}
            <ListSection
                icon={
                    Trophy
                }
                title={
                    text.whyTitle
                }
                introduction={
                    text.whyIntroduction
                }
                items={
                    text.whyItems
                }
            />

            {/* VALAMI MÁS */}
            <ContentSection
                icon={
                    Sparkles
                }
                title={
                    text.differentTitle
                }
                backgroundClass="bg-white"
            >
                <div className="mx-auto mt-8 max-w-[330px] space-y-7 text-[15px] leading-[1.3] text-neutral-700 md:max-w-3xl md:text-[21px] md:leading-[1.5]">
                    <p>
                        {text.differentFirst}
                    </p>

                    <p>
                        {text.differentSecond}
                    </p>

                    <p>
                        {text.differentThird}
                    </p>
                </div>
            </ContentSection>

            {/* ROSSZ IDŐ */}
            <ContentSection
                icon={
                    CloudRain
                }
                title={
                    text.indoorTitle
                }
            >
                <div className="mx-auto mt-8 max-w-[330px] space-y-7 text-[15px] leading-[1.3] text-neutral-700 md:max-w-3xl md:text-[21px] md:leading-[1.5]">
                    <p>
                        {text.indoorDescription}
                    </p>

                    <p>
                        {text.indoorSecond}
                    </p>
                </div>

                <SecondaryButton
                    href={getLocalizedPath(
                        "/belteri-program-budapest",
                        language
                    )}
                >
                </SecondaryButton>
            </ContentSection>

            {/* ALKALMAK */}
            <ListSection
                icon={
                    Sparkles
                }
                title={
                    text.occasionsTitle
                }
                introduction={
                    text.occasionsIntroduction
                }
                items={
                    text.occasionsItems
                }
            />

            {/* PRIVATE ROOM */}
            <ContentSection
                icon={
                    Users
                }
                title={
                    text.privateTitle
                }
                backgroundClass="bg-white"
            >
                <div className="mx-auto mt-8 max-w-[330px] space-y-7 text-[15px] leading-[1.3] text-neutral-700 md:max-w-3xl md:text-[21px] md:leading-[1.5]">
                    <p>
                        {text.privateDescription}
                    </p>

                    <p>
                        {text.privateSecond}
                    </p>
                </div>

                <PrimaryButton
                    href={getLocalizedPath(
                        "/private-room",
                        language
                    )}
                >
                </PrimaryButton>
            </ContentSection>

            <PageImage
                src="/v4/private_room_strike_v3.webp"
                alt="strike private room baráti program és közös élmény Budapesten"
            />

            {/* HELYSZÍN */}
            <ContentSection
                icon={
                    MapPin
                }
                title={
                    text.locationTitle
                }
            >
                <div className="mx-auto mt-8 max-w-[330px] space-y-7 text-[15px] leading-[1.3] text-neutral-700 md:max-w-3xl md:text-[21px] md:leading-[1.5]">
                    <p>
                        {text.locationDescription}
                    </p>

                    <p>
                        {text.locationSecond}
                    </p>
                </div>

                <SecondaryButton
                    href={getLocalizedPath(
                        "/megkozelites",
                        language
                    )}
                >
                </SecondaryButton>
            </ContentSection>

            {/* ZÁRÓ BLOKK */}
            <section className="bg-black text-white">
                <div className="mx-auto flex min-h-[390px] w-full max-w-[430px] flex-col items-center justify-center px-7 py-16 text-center md:max-w-5xl md:min-h-[500px] md:px-12 md:py-20">
                    <Trophy
                        className="h-12 w-12 md:h-16 md:w-16"
                        strokeWidth={
                            2.2
                        }
                    />

                    <h2 className="mt-7 text-[27px] font-black lowercase leading-[1.1] md:text-[48px]">
                        {text.finalTitle}
                    </h2>

                    <p className="mt-7 max-w-[330px] text-[17px] font-black leading-[1.3] md:max-w-2xl md:text-[25px]">
                        {text.finalDescription}
                    </p>

                    <p className="mt-5 max-w-[310px] text-[15px] leading-[1.3] text-white/70 md:max-w-xl md:text-[20px]">
                        {text.finalSecond}
                    </p>

                    <PrimaryButton
                        href="/booking"
                    >
                        {text.finalButton}
                    </PrimaryButton>
                </div>
            </section>

            <Footer />
        </main>
    );
}

function IconCircle({
    children,
}) {
    return (
        <div
            className="mb-6 flex shrink-0 items-center justify-center rounded-full border-[3px] border-black"
            style={{
                width:
                    "80px",

                height:
                    "80px",

                minWidth:
                    "80px",

                maxWidth:
                    "80px",

                minHeight:
                    "80px",

                maxHeight:
                    "80px",

                boxSizing:
                    "border-box",
            }}
        >
            {children}
        </div>
    );
}

function ContentSection({
    icon: Icon,
    title,
    children,
    backgroundClass = "bg-[#dedede]",
}) {
    return (
        <section className={`${backgroundClass} text-black`}>
            <div className="mx-auto flex min-h-[470px] w-full max-w-[430px] flex-col items-center justify-center px-7 py-14 text-center md:max-w-5xl md:min-h-[590px] md:px-12 md:py-20">
                <IconCircle>
                    <Icon
                        style={{
                            width:
                                "38px",

                            height:
                                "38px",
                        }}
                        strokeWidth={
                            2.5
                        }
                    />
                </IconCircle>

                <h2 className="text-[21px] font-black lowercase leading-[1.1] md:text-[36px]">
                    {title}
                </h2>

                {children}
            </div>
        </section>
    );
}

function ListSection({
    icon: Icon,
    title,
    introduction,
    items,
}) {
    return (
        <section className="bg-[#dedede] text-black">
            <div className="mx-auto w-full max-w-[430px] px-5 py-16 md:max-w-5xl md:px-10 md:py-24">
                <div className="border-[3px] border-black bg-[#f2f2f2] px-5 py-10 md:px-12 md:py-14">
                    <div className="flex flex-col items-center text-center">
                        <IconCircle>
                            <Icon
                                style={{
                                    width:
                                        "38px",

                                    height:
                                        "38px",
                                }}
                                strokeWidth={
                                    2.5
                                }
                            />
                        </IconCircle>

                        <h2 className="text-[22px] font-black lowercase leading-[1.1] md:text-[40px]">
                            {title}
                        </h2>

                        <p className="mt-6 max-w-[310px] text-[15px] leading-[1.3] text-neutral-700 md:max-w-xl md:text-[19px] md:leading-[1.45]">
                            {introduction}
                        </p>
                    </div>

                    <div className="mt-10 border-y-[3px] border-black md:mt-14">
                        {items.map(
                            (
                                item,
                                index
                            ) => (
                                <div
                                    key={
                                        item
                                    }
                                    className={`flex items-center gap-4 py-5 md:gap-6 md:py-6 ${index !==
                                        items.length - 1
                                        ? "border-b border-black/25"
                                        : ""
                                        }`}
                                >
                                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-black text-[11px] font-black text-white md:h-11 md:w-11 md:text-[13px]">
                                        {String(
                                            index +
                                            1
                                        ).padStart(
                                            2,
                                            "0"
                                        )}
                                    </span>

                                    <p className="text-[15px] font-semibold leading-[1.4] text-neutral-700 md:text-[19px]">
                                        {item}
                                    </p>
                                </div>
                            )
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}

function PrimaryButton({
    href,
    children,
}) {
    return (
        <a
            href={
                href
            }
            className={`
                ${href === "/booking" ? "button-pulse" : ""} mt-9 flex h-[52px]
                min-w-[240px]
                cursor-pointer select-none
                items-center justify-center
                rounded-[6px]
                bg-[#f5a400] px-7
                text-[18px] font-black
                text-black transition-transform
                duration-200
                hover:scale-[1.03]
                active:translate-y-[2px]
                active:scale-95
                md:mt-11 md:h-[60px]
                md:min-w-[280px]
                md:text-[21px]
            `}
        >
            {children}
        </a>
    );
}

function SecondaryButton({
    href,
    children,
}) {
    return (
        <a
            href={
                href
            }
            className="
                mt-9 flex h-[52px]
                min-w-[220px]
                cursor-pointer select-none
                items-center justify-center
                rounded-[6px]
                border-[3px] border-black
                bg-transparent px-7
                text-[18px] font-black
                text-black transition-transform
                duration-200
                hover:scale-[1.03]
                active:translate-y-[2px]
                active:scale-95
                md:mt-11 md:h-[60px]
                md:min-w-[260px]
                md:text-[21px]
            "
        >
            {children}
        </a>
    );
}

function PageImage({
    src,
    alt,
}) {
    return (
        <section className="w-full overflow-hidden bg-black">
            <div className="relative flex w-full items-center justify-center overflow-hidden bg-black">
                <div
                    aria-hidden="true"
                    className="absolute inset-0 scale-[1.15] bg-cover bg-center blur-[45px]"
                    style={{
                        backgroundImage:
                            `url("${src}")`,
                    }}
                />

                <div
                    aria-hidden="true"
                    className="absolute inset-0 bg-black/20"
                />

                <img
                    src={
                        src
                    }
                    alt={
                        alt
                    }
                    className="relative z-10 block h-auto w-full max-w-[900px] object-contain"
                />
            </div>
        </section>
    );
}